import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Calendar, RefreshCw, Unplug, Link2, CheckCircle2, AlertTriangle, ExternalLink } from 'lucide-react';
import { api } from '@/api';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

export default function GoogleCalendarPanel() {
  const [status, setStatus] = useState(null);
  const [calendars, setCalendars] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get('/admin/gcal/status'); setStatus(data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load Google Calendar status'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!status?.connected) { setCalendars(null); return; }
    api.get('/admin/gcal/calendars').then(({ data }) => setCalendars(data.items || [])).catch(() => setCalendars([]));
  }, [status?.connected, status?.email]);

  const connect = async () => {
    setBusy('connect');
    try { const { data } = await api.get('/admin/gcal/connect-url'); window.location.assign(data.url); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not start Google sign-in'); setBusy(null); }
  };
  const pick = async (calendar_id) => {
    setBusy('pick');
    try { const { data } = await api.put('/admin/gcal/calendar', { calendar_id }); setStatus(data); toast.success(`Bookings will go to “${data.calendar_summary}”.`); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not select calendar'); }
    finally { setBusy(null); }
  };
  const sync = async () => {
    setBusy('sync');
    try { const { data } = await api.post('/admin/gcal/sync'); toast.success(`Synced ${data.synced} of ${data.scanned} bookings${data.errors ? ` · ${data.errors} failed` : ''}.`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Sync failed'); }
    finally { setBusy(null); }
  };
  const pull = async () => {
    setBusy('pull');
    try { const { data } = await api.post('/admin/gcal/pull'); toast.success(`Pulled from Google: ${data.blocks} busy blocks · ${data.moved} moved · ${data.canceled} canceled.`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Pull failed'); }
    finally { setBusy(null); }
  };
  const disconnect = async () => {
    if (!window.confirm('Disconnect Google Calendar? Existing events stay on your calendar; new bookings will stop syncing.')) return;
    setBusy('disconnect');
    try { await api.delete('/admin/gcal/disconnect'); toast.success('Google Calendar disconnected.'); await load(); }
    catch (e) { toast.error('Disconnect failed'); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="admin-gcal-panel">
      <div className="mi-eyebrow mb-3 text-[#E63946] flex items-center gap-2"><Calendar className="w-3 h-3" /> // GOOGLE CALENDAR · BOOKING SYNC</div>
      <div className="border mi-line-strong bg-[#0A0A0A] p-6">
        {!status ? <div className="text-white/50 text-sm">Loading…</div> : !status.configured ? (
          <div className="text-sm text-yellow-300 flex items-start gap-2" data-testid="gcal-not-configured">
            <AlertTriangle className="w-4 h-4 mt-0.5" /> Google OAuth credentials are not configured on this server (GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET).
          </div>
        ) : !status.connected ? (
          <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <div className="text-white text-sm mb-1">Not connected</div>
              <div className="text-white/60 text-xs leading-relaxed">
                Connect the shop's Google account once. Every website booking is then created on the calendar you choose,
                updated when its status changes, and removed when cancelled.
              </div>
              <div className="text-white/35 text-[10px] font-mono mt-2 break-all">Redirect URI: {status.redirect_uri}</div>
            </div>
            <button onClick={connect} disabled={busy === 'connect'} data-testid="gcal-connect-btn" className="mi-btn-red text-xs whitespace-nowrap">
              <Link2 className="w-3.5 h-3.5" /> {busy === 'connect' ? 'Redirecting…' : 'Connect Google Calendar'}
            </button>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 text-sm text-white" data-testid="gcal-connected">
                  <CheckCircle2 className="w-4 h-4 text-[#E63946]" /> Connected as <span className="font-mono">{status.email}</span>
                </div>
                <div className="text-white/50 text-xs mt-1">Since {fmt(status.connected_at)} · last push {fmt(status.last_sync_at)} · last pull {fmt(status.last_pull_at)}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={sync} disabled={busy === 'sync'} data-testid="gcal-sync-btn" className="mi-btn-ghost text-xs">
                  <RefreshCw className={`w-3.5 h-3.5 ${busy === 'sync' ? 'animate-spin' : ''}`} /> Push bookings
                </button>
                <button onClick={pull} disabled={busy === 'pull'} data-testid="gcal-pull-btn" className="mi-btn-ghost text-xs">
                  <RefreshCw className={`w-3.5 h-3.5 ${busy === 'pull' ? 'animate-spin' : ''}`} /> Pull from Google
                </button>
                <button onClick={disconnect} disabled={busy === 'disconnect'} data-testid="gcal-disconnect-btn" className="mi-btn-ghost text-xs text-white/60">
                  <Unplug className="w-3.5 h-3.5" /> Disconnect
                </button>
              </div>
            </div>

            <div className="grid md:grid-cols-[1fr_auto] gap-4 items-end">
              <label className="block">
                <div className="mi-eyebrow text-white/60 text-[10px] mb-1.5">Bookings go to this calendar</div>
                <select value={status.calendar_id || ''} onChange={(e) => pick(e.target.value)} disabled={!calendars || busy === 'pick'}
                        data-testid="gcal-calendar-select" className="mi-input">
                  {!calendars && <option value={status.calendar_id || ''}>{status.calendar_summary || 'Loading calendars…'}</option>}
                  {calendars?.map((c) => <option key={c.id} value={c.id}>{`${c.summary}${c.primary ? ' (primary)' : ''}`}</option>)}
                </select>
              </label>
              <a href="https://calendar.google.com" target="_blank" rel="noopener noreferrer" className="mi-btn-ghost text-xs">
                Open Google Calendar <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat k="Upcoming bookings" v={status.upcoming_total} />
              <Stat k="On Google Calendar" v={status.upcoming_synced} />
              <Stat k="Blocked by Google events" v={status.blocks_upcoming} />
              <Stat k="Sync errors" v={status.errors} warn={status.errors > 0} />
            </div>
            <div className="text-white/45 text-[11px] leading-relaxed">
              Two-way: events you add on this calendar block those times on the website booking widget (every 15 min, or pull now).
              Moving a website booking in Google updates it here; deleting it cancels the booking.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ k, v, warn }) {
  return (
    <div className="border mi-line p-3 bg-black/40">
      <div className="mi-eyebrow text-[9px] text-white/50 mb-1">{k}</div>
      <div className={`mi-headline text-2xl ${warn ? 'text-yellow-400' : 'text-white'}`}>{v ?? '—'}</div>
    </div>
  );
}
